import { createFileRoute, Link } from "@tanstack/react-router";
import { Fragment, useState } from "react";
import { toast } from "sonner";
import { brl, data, statusLabel, tipoLabel } from "@/lib/format";
import {
  PageHeader,
  Panel,
  Th,
  Td,
  EmptyState,
  StatusBadge,
  Escolha,
  Carregando,
  Erro,
} from "@/components/parker";
import {
  useEmpresas,
  useCategorias,
  useCentros,
  useLancamentos,
  useRateios,
  usePorLancamento,
  useIndice,
} from "@/lib/dados";
import { marcarPago, estornarLancamento } from "@/lib/fns";
import { baixarCSV, centavosParaCSV } from "@/lib/exportar";
import { useSessao } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Historico } from "@/components/historico";
import { Rateio } from "@/components/rateio";
import { Anexos } from "@/components/anexos";
import { History, Split, Download, Paperclip } from "lucide-react";
import type { Lancamento } from "@/lib/tipos";

export const Route = createFileRoute("/_authenticated/lancamentos/")({ component: Lancamentos });

function Lancamentos() {
  const { podeEscrever } = useSessao();
  const empresas = useEmpresas();
  const categorias = useCategorias();
  const centros = useCentros();
  const lancamentos = useLancamentos();
  const rateios = useRateios();

  const rateioPorLancamento = usePorLancamento(rateios.dados);
  const nomeEmpresa = useIndice(empresas.dados);
  const nomeCategoria = useIndice(categorias.dados);
  const nomeCentro = useIndice(centros.dados);

  const [fStatus, setFStatus] = useState("");
  const [fEmpresa, setFEmpresa] = useState("");
  const [busca, setBusca] = useState("");
  const [ocupado, setOcupado] = useState<string | null>(null);
  // Qual linha está com o histórico aberto.
  const [aberto, setAberto] = useState<string | null>(null);
  const [rateando, setRateando] = useState<string | null>(null);
  const [anexando, setAnexando] = useState<string | null>(null);

  if (lancamentos.erro) return <Erro mensagem={lancamentos.erro} />;

  async function pagar(l: Lancamento) {
    setOcupado(l.id);
    try {
      await marcarPago({ id: l.id });
      toast.success("Lançamento marcado como pago/recebido.");
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setOcupado(null);
    }
  }

  async function estornar(l: Lancamento) {
    setOcupado(l.id);
    try {
      await estornarLancamento({ id: l.id });
      toast.success("Estorno criado como rascunho e ligado ao original.");
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setOcupado(null);
    }
  }

  /** Exporta o que está na tela, com os filtros aplicados — não a base inteira. */
  function exportar() {
    baixarCSV(
      "lancamentos",
      [
        "Competência",
        "Caixa",
        "Descrição",
        "Tipo",
        "Paga",
        "Beneficiada",
        "Centro de custo",
        "Categoria",
        "Valor",
        "Status",
        "Origem",
        "Rateado",
      ],
      linhas.map((l) => [
        l.dataCompetencia,
        l.dataCaixa ?? "",
        l.descricao,
        tipoLabel[l.tipo] ?? l.tipo,
        nomeEmpresa.get(l.empresaPagadoraId) ?? "",
        nomeEmpresa.get(l.empresaBeneficiadaId) ?? "",
        nomeCentro.get(l.centroCustoId) ?? "",
        nomeCategoria.get(l.categoriaId) ?? "",
        centavosParaCSV(l.valorCentavos),
        statusLabel[l.status] ?? l.status,
        l.origem,
        l.temRateio ? "sim" : "não",
      ]),
    );
  }

  const termo = busca.trim().toLowerCase();
  const linhas = lancamentos.dados.filter(
    (l) =>
      (fStatus === "" || l.status === fStatus) &&
      (fEmpresa === "" ||
        l.empresaBeneficiadaId === fEmpresa ||
        l.empresaPagadoraId === fEmpresa) &&
      (termo === "" || l.descricao.toLowerCase().includes(termo)),
  );

  return (
    <>
      <PageHeader
        title="Lançamentos"
        subtitle="Quem paga e quem se beneficia são registrados separadamente."
        actions={
          <>
            <Button variant="outline" onClick={exportar} disabled={linhas.length === 0}>
              <Download className="size-4" /> Exportar
            </Button>
            {podeEscrever ? (
              <Button asChild>
                <Link to="/lancamentos/novo">Novo lançamento</Link>
              </Button>
            ) : null}
          </>
        }
      />

      <div className="mb-6 flex flex-col gap-2 sm:flex-row sm:flex-wrap">
        <Input
          placeholder="Buscar por descrição"
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          className="w-full sm:max-w-xs"
        />
        <Escolha
          className="w-full sm:w-56"
          value={fStatus}
          onChange={setFStatus}
          permitirVazio
          placeholder="Todos os status"
          options={Object.entries(statusLabel)
            .filter(([k]) => !["aberto", "liquidado", "rejeitado"].includes(k))
            .map(([id, label]) => ({ id, label }))}
        />
        <Escolha
          className="w-full sm:w-64"
          value={fEmpresa}
          onChange={setFEmpresa}
          permitirVazio
          placeholder="Todas as empresas"
          options={empresas.dados.map((e) => ({ id: e.id, label: e.nome }))}
        />
      </div>

      <Panel className="overflow-x-auto">
        {lancamentos.carregando ? (
          <Carregando linhas={6} />
        ) : (
          <>
            <table className="w-full min-w-[1040px] text-sm">
              <thead>
                <tr>
                  <Th>Competência</Th>
                  <Th>Descrição</Th>
                  <Th>Tipo</Th>
                  <Th>Paga</Th>
                  <Th>Beneficiada</Th>
                  <Th>Centro de custo</Th>
                  <Th>Categoria</Th>
                  <Th className="text-right">Valor</Th>
                  <Th>Status</Th>
                  <Th />
                </tr>
              </thead>
              <tbody>
                {linhas.map((l) => (
                  <Fragment key={l.id}>
                    <tr className="hover:bg-surface-2/60">
                      <Td className="num whitespace-nowrap">{data(l.dataCompetencia)}</Td>
                      <Td>
                        <div className="max-w-[260px] truncate">{l.descricao}</div>
                        {l.estornoDe ? (
                          <span className="label-caps">estorno vinculado</span>
                        ) : l.origem === "contrato" ? (
                          <span className="label-caps">parcela de contrato</span>
                        ) : l.origem === "mensagem" ? (
                          <span className="label-caps">classificado por IA</span>
                        ) : null}
                        {l.temRateio ? (
                          <span className="label-caps text-emerald">rateado</span>
                        ) : null}
                      </Td>
                      <Td className="text-muted-foreground">{tipoLabel[l.tipo]}</Td>
                      <Td className="text-muted-foreground">
                        {nomeEmpresa.get(l.empresaPagadoraId) ?? "—"}
                      </Td>
                      <Td>{nomeEmpresa.get(l.empresaBeneficiadaId) ?? "—"}</Td>
                      <Td className="text-muted-foreground">
                        {nomeCentro.get(l.centroCustoId) ?? "—"}
                      </Td>
                      <Td className="text-muted-foreground">
                        {nomeCategoria.get(l.categoriaId) ?? "—"}
                      </Td>
                      <Td
                        className={`num text-right ${
                          l.valorCentavos < 0
                            ? "text-muted-foreground"
                            : l.tipo === "receita"
                              ? "text-positive"
                              : "text-foreground"
                        }`}
                      >
                        {brl(l.valorCentavos)}
                      </Td>
                      <Td>
                        <StatusBadge status={l.status} />
                      </Td>
                      <Td>
                        <div className="flex justify-end gap-1">
                          {podeEscrever && l.tipo !== "nao_operacional" ? (
                            <Button
                              size="sm"
                              variant="ghost"
                              title="Ratear entre empresas"
                              onClick={() => setRateando((r) => (r === l.id ? null : l.id))}
                            >
                              <Split className="size-3.5" />
                            </Button>
                          ) : null}
                          <Button
                            size="sm"
                            variant="ghost"
                            title="Evidências deste lançamento"
                            onClick={() => setAnexando((a) => (a === l.id ? null : l.id))}
                          >
                            <Paperclip className="size-3.5" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            title="Histórico deste lançamento"
                            onClick={() => setAberto((a) => (a === l.id ? null : l.id))}
                          >
                            <History className="size-3.5" />
                          </Button>
                          {podeEscrever && l.status === "aprovado" ? (
                            <Button
                              size="sm"
                              variant="secondary"
                              disabled={ocupado === l.id}
                              onClick={() => void pagar(l)}
                            >
                              Pagar
                            </Button>
                          ) : null}
                          {podeEscrever &&
                          !l.estornoDe &&
                          ["aprovado", "pago", "conciliado"].includes(l.status) ? (
                            <Button
                              size="sm"
                              variant="ghost"
                              disabled={ocupado === l.id}
                              onClick={() => void estornar(l)}
                            >
                              Estornar
                            </Button>
                          ) : null}
                        </div>
                      </Td>
                    </tr>
                    {anexando === l.id ? (
                      <tr>
                        <td colSpan={10} className="border-b border-hairline bg-surface-2/40 p-0">
                          <Anexos lancamentoId={l.id} podeEditar={podeEscrever} />
                        </td>
                      </tr>
                    ) : null}
                    {rateando === l.id ? (
                      <tr>
                        <td colSpan={10} className="border-b border-hairline bg-surface-2/40 p-0">
                          <Rateio
                            lancamentoId={l.id}
                            valorCentavos={l.valorCentavos}
                            atuais={rateioPorLancamento.get(l.id) ?? []}
                            podeEditar={podeEscrever}
                          />
                        </td>
                      </tr>
                    ) : null}
                    {aberto === l.id ? (
                      <tr>
                        <td colSpan={10} className="border-b border-hairline bg-surface-2/40 p-0">
                          <div className="px-2 py-1">
                            <div className="label-caps px-3 pt-2">Histórico do lançamento</div>
                            <Historico entidade="lancamentos" entidadeId={l.id} />
                          </div>
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                ))}
              </tbody>
            </table>
            {linhas.length === 0 ? (
              <EmptyState
                title="Nenhum lançamento"
                hint={
                  lancamentos.dados.length > 0
                    ? "Nenhum lançamento bate com os filtros."
                    : "Crie o primeiro lançamento do grupo."
                }
              />
            ) : null}
          </>
        )}
      </Panel>
    </>
  );
}
